import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isoLocal, periodoAnterior, type Rango } from "./format";
import { supa } from "./supabase";

export type OpcionRango = "hoy" | "ayer" | "7" | "30" | "90" | "custom";

interface FiltrosCtx {
  opcion: OpcionRango;
  setOpcion: (o: OpcionRango) => void;
  custom: { desde: string; hasta: string };
  setCustom: (c: { desde: string; hasta: string }) => void;
  sucursal: number | null;
  setSucursal: (s: number | null) => void;
  rango: Rango;
  rangoPrevio: Rango;
}

const Ctx = createContext<FiltrosCtx | null>(null);

/** Misma lógica que rangoFechas() de app.js (líneas 733-755). */
function calcularRango(
  opcion: OpcionRango,
  custom: { desde: string; hasta: string },
): Rango {
  if (opcion === "hoy" || opcion === "ayer") {
    const d = new Date();
    if (opcion === "ayer") d.setDate(d.getDate() - 1);
    const dia = isoLocal(d);
    return { desde: dia, hasta: dia };
  }
  if (opcion === "custom" && custom.desde && custom.hasta) {
    let { desde, hasta } = custom;
    if (desde > hasta) [desde, hasta] = [hasta, desde]; // por si las invierten
    return { desde, hasta };
  }
  const dias = Number(opcion) || 30;
  const hasta = new Date();
  const desde = new Date(Date.now() - dias * 864e5);
  return { desde: isoLocal(desde), hasta: isoLocal(hasta) };
}

export function FiltrosProvider({ children }: { children: ReactNode }) {
  const [opcion, setOpcion] = useState<OpcionRango>("30");
  const [custom, setCustom] = useState({ desde: "", hasta: "" });
  const [sucursal, setSucursal] = useState<number | null>(null);

  // Al cerrar sesión (a mano o porque venció) los filtros vuelven a su estado
  // inicial. Si no, en una computadora compartida el siguiente que entre hereda
  // el rango y la SUCURSAL del anterior: el servidor rechazará una sucursal que
  // no le toque, pero la pantalla ya le estaría diciendo que está viendo CDMX.
  useEffect(() => {
    const { data: sub } = supa.auth.onAuthStateChange((_e, s) => {
      if (!s) {
        setOpcion("30");
        setCustom({ desde: "", hasta: "" });
        setSucursal(null);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const rango = useMemo(() => calcularRango(opcion, custom), [opcion, custom]);
  const rangoPrevio = useMemo(
    () => periodoAnterior(rango.desde, rango.hasta),
    [rango],
  );

  const valor = useMemo(
    () => ({
      opcion, setOpcion, custom, setCustom,
      sucursal, setSucursal, rango, rangoPrevio,
    }),
    [opcion, custom, sucursal, rango, rangoPrevio],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useFiltros(): FiltrosCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useFiltros fuera de <FiltrosProvider>");
  return c;
}
